import {LOCAL_PAGES} from '@product/quies/data/local-pages/registry'
import {HOME_LANGS, type HomeLang} from './homeLangs'

export const LANGUAGES = HOME_LANGS
export type Lang = HomeLang

// Map of topic -> language -> slug
export const HREFLANG_MAP: Record<string, Record<string, string>> = {
    home: Object.fromEntries(HOME_LANGS.map((lang) => [lang, lang === 'en' ? '/' : `/${lang}`])),
    // Condition pages — English only
    anxiety: {en: '/hypnosis-for-anxiety'},
    sleep: {en: '/hypnosis-for-sleep'},
    stress: {en: '/hypnosis-for-stress'},
    focus: {en: '/hypnosis-for-focus'},
    confidence: {en: '/hypnosis-for-confidence'},
    // Method pages — English only
    ericksonian: {en: '/what-is-ericksonian-hypnosis'},
    'ai-how-it-works': {en: '/ai-hypnosis-how-it-works'},
    'vs-meditation': {en: '/hypnosis-vs-meditation'},
}

/**
 * Inverted index: slug -> lang. Built once at module load from HREFLANG_MAP and the local pages, which have a
 * language but no alternates.
 */
const SLUG_TO_LANG: Record<string, string> = {}
for (const variants of Object.values(HREFLANG_MAP)) {
    for (const [lang, slug] of Object.entries(variants)) {
        SLUG_TO_LANG[slug] = lang
    }
}
for (const {path, lang} of Object.values(LOCAL_PAGES)) {
    SLUG_TO_LANG[path] = lang
}

/**
 * Detect language from a URL pathname.
 * Matches against every slug in the hreflang map; falls back to 'en'.
 */
export function detectLangFromPath(pathname: string): string {
    return SLUG_TO_LANG[pathname] ?? 'en'
}

export const BASE_URL = 'https://quies.ai'

/** A page's own home: its language's, or `/` for a language that has none. */
export function homeFor(lang: string): string {
    return HREFLANG_MAP.home[lang] ?? '/'
}

/**
 * The translation-only homes, retired (search estate plan, decision 2): each answers 301 to `/`; none of their
 * languages has a local page. `ja` and `zh` left the list on 2026-11-01: they have homes.
 */
export const RETIRED_HOME_LANGS = ['ar', 'hi', 'ko', 'pt', 'ru', 'tr'] as const

export function retiredHomeTarget(param: string | undefined): string | null {
    return RETIRED_HOME_LANGS.some((l) => l === param?.toLowerCase()) ? '/' : null
}

/**
 * The home page's language for the optional `/:lang?` segment, or null when the path is
 * no home page (`/en`, `/xx`). Case-insensitive: `/FR` served the French home before the
 * fixed per-language routes became one, and still does (its canonical names `/fr`).
 */
export function homeLang(param: string | undefined): Lang | null {
    const path = param === undefined ? '/' : `/${param.toLowerCase()}`
    const match = Object.entries(HREFLANG_MAP.home).find(([, slug]) => slug === path)
    return match ? (match[0] as Lang) : null
}

/**
 * The hreflang links of a topic's page: one per language variant, plus x-default (en). A topic in one language has
 * no alternate, so it gets none.
 */
export function getHreflangMeta(topic: string) {
    const variants = HREFLANG_MAP[topic]
    if (!variants || Object.keys(variants).length < 2) return []
    return Object.entries(variants).flatMap(([lang, slug]) => {
        const entries: Array<{tagName: 'link'; rel: string; hreflang: string; href: string}> = [
            {tagName: 'link', rel: 'alternate', hreflang: lang, href: `${BASE_URL}${slug}`},
        ]
        if (lang === 'en') {
            entries.push({tagName: 'link', rel: 'alternate', hreflang: 'x-default', href: `${BASE_URL}${slug}`})
        }
        return entries
    })
}
